import { computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { validate as uuidValidate } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { BreakModalComponent } from '@connect-services/client/components/break-modal/break-modal.component';
import { ModalComponent } from '@connect-services/client/components/modal/modal.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    HighlightEntityBroadcastChannelData,
    LayerBroadCastChannelData,
    LayerBroadcastChannelTargetEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeInformationService } from '@connect-services/client/services/attribute-information.service';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { DocumentIdentifiers, DocumentService } from '@connect-services/client/services/document.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { excludedAttributes } from '@connect-services/client/services/layer.service';
import { EntityLocation, MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { NetworkPathService } from '@connect-services/client/services/network-path/network-path.service';
import { OpenableBoxOrBayPlanService } from '@connect-services/client/services/openable-box-or-bay-plan.service';
import { OpticalPathExportService } from '@connect-services/client/services/optical-path-export.service';
import { COLUMN_NAMES_BATCH_SIZE } from '@connect-services/client/services/project-entity-table.service';
import { RouterService } from '@connect-services/client/services/router.service';
import {
    Column,
    ColumnsService,
    Deposit,
    DepositsService,
    Geometry,
    InformationSchemaTableExtended,
    Layer,
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntityAdditionalProperties,
    LayersService,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface AttributeValue {
    key: string;
    order?: number | null;
    value: unknown;
}

export type CenterEntityContext = {
    geom: Geometry;
    geomBboxEntity: EntityLocation;
    layer: Layer;
    layerEntity: DataEntityAdditionalProperties;
};

export abstract class AbstractEntityDetailComponent {
    public readonly bookmarkedAttributes = signal<AttributeValue[]>([]);
    public readonly bookmarks = signal<DataEntityAdditionalProperties | undefined>(undefined);
    public readonly columnsByName = signal<Map<string, Column>>(new Map());
    public readonly layerAttributesInformation = signal<InformationSchemaTableExtended[]>([]);

    protected readonly businessIdentifierKey = signal<string | undefined>(undefined);
    protected readonly canReadDocuments = computed<boolean>(
        () =>
            !!this.layerEntity() &&
            this.canUseDocuments() &&
            this.isFromAStandardizedDeposit() &&
            this.layer()?.type !== this.TypeEnum.INTERSTANDARD &&
            this.layer()?.type !== this.TypeEnum.WFS,
    );
    protected readonly canUseDocuments = computed<boolean>(() => this.documentService.canAccessDocument());
    protected readonly dataEntity = computed<EntityProperties>(() => {
        return {
            ...this.layerEntity(),
            layerId: this.layer()?.id ?? '',
        } as EntityProperties;
    });
    protected readonly deposit = signal<Deposit | null>(null);
    protected readonly documentData = computed<DocumentIdentifiers>(() => {
        return {
            depositId: this.layer()?.deposit ?? '',
            lost: false,
            technicalIdentifier: this.layerEntity()?.['technicalIdentifier'] ?? '',
        };
    });
    protected readonly highlightBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.HIGHLIGHT);
    protected identifier!: string;
    protected readonly isBoxOrBayPlanAvailable = computed<boolean>(() => {
        const layer = this.layer();
        const dataEntity = this.dataEntity();

        return !!layer?.openableBoxOrBayPlan && uuidValidate(dataEntity.uuid);
    });
    protected readonly isEntityUsedInBreak = computed<boolean>(() => !!this.layer()?.isUsedInBreak);
    protected readonly isFromAStandardizedDeposit = signal<boolean>(false);
    protected readonly isLoading = signal<boolean>(true);
    protected readonly isOpticalPathAvailable = signal<boolean>(false);
    protected readonly isSynopticAvailable = computed<boolean>(() => {
        const layer = this.layer();
        const dataEntity = this.dataEntity();
        const znUuid = this.getZnUuidAttributeValue(dataEntity);

        const hasValidLayer = !!layer && layer['isUsedInSynoptic'];
        const hasValidZnUuid = !!znUuid && !!Object.keys(znUuid).length;

        return hasValidLayer && hasValidZnUuid;
    });
    protected readonly layer = signal<Layer | null>(null);
    protected readonly layerEntity = signal<DataEntityAdditionalProperties | null>(null);
    protected readonly layersBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    protected readonly notBookmarkedAttributes = signal<AttributeValue[]>([]);
    protected readonly router = inject(Router);
    protected readonly TypeEnum = Layer.TypeEnum;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly broadcastChannelService = inject(BroadcastChannelService);
    protected readonly documentService = inject(DocumentService);
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected readonly homeService = inject(HomeService);
    protected readonly layerService = inject(LayersService);
    protected readonly mapDrawService = inject(MapDrawService);
    protected readonly projectBloc = inject(ProjectBloc);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly routerService = inject(RouterService);
    protected readonly snackBarService = inject(SnackBarService);

    private readonly attributeInformationService = inject(AttributeInformationService);
    private readonly columnsService = inject(ColumnsService);
    private readonly depositsService = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly matDialog = inject(MatDialog);
    private readonly networkPathService = inject(NetworkPathService);
    private readonly openableBoxOrBayPlanService = inject(OpenableBoxOrBayPlanService);
    private readonly opticalPathExportService = inject(OpticalPathExportService);
    private readonly translocoService = inject(TranslocoService);

    protected constructor() {
        window.addEventListener('beforeunload', () => this.resetHighlightChannel());

        this.destroyRef.onDestroy(async () => {
            if (!this.layer() || !this.layerEntity()) {
                return;
            }

            const { source, entityType } = this.mapDrawService.getSourceAndEntityType(
                this.layer()!.id,
                this.layerEntity()!.geom,
            );
            const urlParams = this.routerService.getUrlParams();

            if (!urlParams.has(RoutePathEnum.EXT, 'true') || !source) {
                await this.mapHighlightEntityService.resetHighlightAfterMapReady(source, entityType);

                return;
            }

            window.removeEventListener('beforeunload', () => this.resetHighlightChannel());
            this.highlightBroadcastChannel.close();
        });

        effect(() => {
            if (!this.layerEntity()) {
                return;
            }

            const { source, entityType } = untracked(() =>
                this.mapDrawService.getSourceAndEntityType(this.layer()!.id, this.layerEntity()!.geom),
            );
            const urlParams = this.routerService.getUrlParams();

            if (!urlParams.has(RoutePathEnum.EXT, 'true') || !source) {
                return;
            }

            this.highlightBroadcastChannel.postMessage(<BroadcastChannelEvent<HighlightEntityBroadcastChannelData>>{
                additionalData: {
                    entityId: this.layerEntity()?.uuid,
                    entityType,
                    source,
                },
                message: BroadcastChannelMessageEnum.HIGHLIGHT_ENTITY,
                source: BroadcastChannelSourceEnum.LAYER,
            });
        });

        effect(async () => {
            if (!this.layer()) {
                return;
            }
            const attributesInformations: InformationSchemaTableExtended[] =
                await this.attributeInformationService.loadForLayer(this.layer()!.id);

            this.layerAttributesInformation.set(attributesInformations);
        });
    }

    public async exportOpticalPath(): Promise<void> {
        await this.opticalPathExportService.exportOpticalPath(
            this.layer()!.deposit!,
            this.layerEntity()!.uuid,
            this.layerEntity()!.businessIdentifier,
        );
    }

    public navigateToNetworkPath(): void {
        const entityId: string | undefined = this.layerEntity()?.uuid;
        const layerId: string | undefined = this.layer()?.id;

        if (!entityId || !layerId) {
            return;
        }

        void this.networkPathService.networkPathRedirection(layerId, entityId);
    }

    public async openBoxOrBayPlan(): Promise<void> {
        const layer = this.layer();
        const dataEntity = this.dataEntity();
        const znUuidAttributeValue = this.getZnUuidAttributeValue(dataEntity);
        const entityUuid = dataEntity.uuid;

        if (!layer?.deposit || !layer.openableBoxOrBayPlan || !entityUuid) {
            this.snackBarService.openErrorSnackBar('noEntitySynoptic');

            return;
        }

        AssertService.isString(entityUuid);

        await this.openPlanWithExistingWindowSafety(layer, dataEntity, znUuidAttributeValue);
    }

    public openBreakModal(): void {
        if (!this.deposit() || !this.layer()) {
            return;
        }

        this.matDialog.open(BreakModalComponent, {
            data: { depositId: this.deposit()!.id, entityId: this.dataEntity().uuid, layerId: this.layer()!.id },
            width: '35rem',
        });
    }

    public openSynopticPanel(): void {
        const dataEntity = this.dataEntity();
        const znUuidAttributeValue = this.getZnUuidAttributeValue(dataEntity);

        if (!znUuidAttributeValue) {
            this.snackBarService.openErrorSnackBar('noEntitySynoptic');

            return;
        }
        AssertService.isString(znUuidAttributeValue);

        if (this.broadcastChannelService.isSynopticOpenInExternalPage()) {
            if (!this.mapEditModeService.isSynopticPlanEditModeActive()) {
                this.openBoxPlanOnExternalPage(znUuidAttributeValue);

                return;
            }

            const dialogRef = this.matDialog.open(ModalComponent, {
                data: {
                    cancelLabel: this.translocoService.translate('cancel'),
                    confirmLabel: this.translocoService.translate('confirm'),
                    description: this.translocoService.translate('closeBoxPlanInformation'),
                    title: this.translocoService.translate('confirmContinue'),
                    type: ModalType.INFO,
                },
            });

            dialogRef.afterClosed().subscribe((result: ModalResultEnum) => {
                if (result !== ModalResultEnum.CONFIRM) {
                    return;
                }

                const popup = window.open('', 'plan');
                popup?.focus();
                this.openBoxPlanOnExternalPage(znUuidAttributeValue);
            });

            return;
        }

        const layer = this.layer();
        if (!layer?.deposit) {
            return;
        }

        this.broadcastChannelService.openSynopticInExternalWindow({
            dataEntity: this.dataEntity(),
            deposit: layer.deposit,
            target: LayerBroadcastChannelTargetEnum.EXTERNAL_SYNOPTIC,
            znUuidAttributeValue,
        });
    }

    protected getDeposit(depositId: string): Promise<Deposit> {
        return firstValueFrom(this.depositsService.depositControllerGetOne({ id: depositId }));
    }

    protected isEntityExcluded(key: string, value: unknown): boolean {
        return this.editEntityDetailService.isEditMode()
            ? excludedAttributes.includes(key)
            : excludedAttributes.includes(key) || !value;
    }

    protected setAttributes(bookmarked: AttributeValue[], notBookmarked: AttributeValue[]): void {
        const sortedBookmarked = bookmarked.toSorted((a, b) => (a.order || 0) - (b.order || 0));
        const sortedNotBookmarked = notBookmarked.toSorted((a, b) => a.key.localeCompare(b.key));

        this.bookmarkedAttributes.set(sortedBookmarked);
        this.notBookmarkedAttributes.set(sortedNotBookmarked);
    }

    protected async setIsFromAStandardizedDeposit(): Promise<void> {
        if (this.layer()?.type === Layer.TypeEnum.INTERSTANDARD) {
            this.isFromAStandardizedDeposit.set(true);
        }

        if (this.layer()?.type === Layer.TypeEnum.TABLE && this.layer()?.deposit) {
            this.deposit.set(await this.getDeposit(this.layer()!.deposit!));

            this.isFromAStandardizedDeposit.set(!!this.deposit()!.standard);
        }

        await this.loadColumns();
    }

    private async fetchColumns(columnNames: string[], standard: string, tableName: string): Promise<Column[]> {
        const columnNamesBatches = this.getColumnNamesBatches(columnNames);
        const columnsByBatch = await Promise.all(
            columnNamesBatches.map((columnNamesBatch: string[]) =>
                firstValueFrom(
                    this.columnsService.columnControllerGetByName({
                        columnNames: columnNamesBatch,
                        standard,
                        tableName,
                    }),
                ),
            ),
        );

        return columnsByBatch.flat();
    }

    private getColumnNames(): string[] {
        return [
            ...new Set(
                [...this.bookmarkedAttributes(), ...this.notBookmarkedAttributes()].map(
                    (attribute: AttributeValue) => attribute.key,
                ),
            ),
        ];
    }

    private getColumnNamesBatches(columnNames: string[]): string[][] {
        const batches: string[][] = [];

        for (let index = 0; index < columnNames.length; index += COLUMN_NAMES_BATCH_SIZE) {
            batches.push(columnNames.slice(index, index + COLUMN_NAMES_BATCH_SIZE));
        }

        return batches;
    }

    private getZnUuidAttributeValue(dataEntity: EntityProperties): string | undefined {
        const znUuid = dataEntity ? dataEntity['zn_uuid'] : undefined;

        return typeof znUuid === 'string' && znUuid.length ? znUuid : undefined;
    }

    private async loadColumns(): Promise<void> {
        const layer = this.layer();
        const standard = this.deposit()?.standard;

        if (!layer?.tableName || !standard) {
            this.columnsByName.set(new Map());

            return;
        }

        const columnNames = this.getColumnNames();

        if (!columnNames.length) {
            this.columnsByName.set(new Map());

            return;
        }

        try {
            const columns = await this.fetchColumns(columnNames, standard, layer.tableName);

            this.columnsByName.set(new Map(columns.map((column: Column) => [column.name, column])));
        } catch {
            this.columnsByName.set(new Map());
        }
    }

    private openBoxPlanOnExternalPage(znUuidAttributeValue: string): void {
        const layer = this.layer();
        if (!layer?.deposit) {
            return;
        }

        this.layersBroadcastChannel.postMessage(<BroadcastChannelEvent<LayerBroadCastChannelData>>{
            additionalData: {
                dataEntity: this.dataEntity(),
                deposit: layer.deposit,
                target: LayerBroadcastChannelTargetEnum.EXTERNAL_SYNOPTIC,
                znUuidAttributeValue,
            },
            message: BroadcastChannelMessageEnum.MOVE_SYNOPTIC,
            source: BroadcastChannelSourceEnum.LAYER,
        });
    }

    private async openPlanWithExistingWindowSafety(
        layer: Layer,
        entity: EntityProperties,
        znUuidAttributeValue?: string,
    ): Promise<void> {
        if (!this.broadcastChannelService.isSynopticOpenInExternalPage()) {
            await this.openableBoxOrBayPlanService.openInExternalWindow(layer, entity, znUuidAttributeValue);

            return;
        }

        if (!this.mapEditModeService.isSynopticPlanEditModeActive()) {
            await this.openableBoxOrBayPlanService.openInExternalWindow(layer, entity, znUuidAttributeValue);

            return;
        }

        const dialogRef = this.matDialog.open(ModalComponent, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('confirm'),
                description: this.translocoService.translate('closeBoxPlanInformation'),
                title: this.translocoService.translate('confirmContinue'),
                type: ModalType.INFO,
            },
        });

        dialogRef.afterClosed().subscribe((result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            void this.openableBoxOrBayPlanService.openInExternalWindow(layer, entity, znUuidAttributeValue);
        });
    }

    private resetHighlightChannel(): void {
        const { source, entityType } = this.mapDrawService.getSourceAndEntityType(
            this.layer()!.id,
            this.layerEntity()!.geom,
        );

        if (!source) {
            return;
        }

        this.highlightBroadcastChannel.postMessage(<BroadcastChannelEvent<HighlightEntityBroadcastChannelData>>{
            additionalData: {
                entityId: this.layerEntity()?.uuid,
                entityType,
                source,
            },
            message: BroadcastChannelMessageEnum.RESET_HIGHLIGHT_ENTITY,
            source: BroadcastChannelSourceEnum.LAYER,
        });
    }

    protected abstract initLayerEntity(): Promise<void>;

    protected abstract setLayerDataAttributes(): Promise<void>;
}
